import { useContext } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AppError, Loader } from "@bfc-software/pallet";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { validatePageSearch } from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";

import {
  DEFAULT_TASK_SCOPE,
  TASK_SCOPES,
  TaskScope,
  useTaskFilters,
  validateTaskScope,
} from "../../-data/filters";
import { useTaskList } from "../../-data/use-tasks";
import { useTaskDrawerSelection } from "../../-data/use-task-drawer-selection";
import { ScopeChips, type ScopeChip } from "../../-components/scope-chips";
import { TaskDetailDrawer } from "../../-components/task-detail-drawer";
import { TaskTable } from "../../-components/task-table";
import styles from "../../tasks.module.css";

import { tasksPageStateFrom } from "./-state";

/**
 * All tasks: the floor as it stands.
 *
 * Its own route and its own read (`use-tasks`), which the plans tab does not
 * touch. Nothing on this tab writes - the one write on this screen is plan
 * activation, which belongs to the other tab.
 *
 * A directory rather than a file, so `-state.ts` can sit next to it: the union
 * the component renders is this route's, not something the whole feature shares,
 * and a `-data` file would put it where the other tab could reach for it.
 *
 * What this tab shows is the *activation* listing, so it can only see tasks
 * somebody released. A plan nobody has activated has no rows here at all, which
 * the note above the table says out loud rather than leaving a manager to work
 * out from an absence. It is the one thing on this tab the service cannot
 * answer: every filter in the bar is a param on the single request.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/tasks/_layout/all/",
)({
  /**
   * The page cursor, owned by the leaf.
   *
   * The filters and the chip belong to the layout above (`validateTaskFiltersSearch`),
   * because both tabs read them; a cursor names a boundary in *this* listing's
   * result set, so it means nothing on the plans tab and is not carried there.
   * That split is also what returns a narrowed view to page 1 - the layout's
   * `setSearch` rebuilds the params from its own schema, and this pair is simply
   * not in it.
   */
  validateSearch: validatePageSearch,
  component: RouteComponent,
});

function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  /**
   * The org the requests are made against.
   *
   * The URL carries the organisation's stub; the task planner's path segment is
   * the Stytch org id. `null` while a session is being minted, which the read
   * waits on rather than requesting `/undefined/task-activations`.
   */
  const orgId = AuthUtils.getOrgIdByStub(authState, org);

  const { filters, search, setSearch } = useTaskFilters();
  const page = Route.useSearch();
  const navigate = Route.useNavigate();

  const scope = search.scope ?? DEFAULT_TASK_SCOPE;

  const query = useTaskList({
    orgId,
    warehouseId: location,
    filters,
    scope,
    page,
  });
  const state = tasksPageStateFrom(query, filters);

  /**
   * The open task detail drawer, held exactly as the plans tab holds it - the
   * identity in the URL, the clicked row in state (see
   * `useTaskDrawerSelection`). Every task this tab can list is released, so the
   * drawer opened from here always has requirements to draw.
   */
  const drawer = useTaskDrawerSelection();

  /*
    The chips carry no counts.

    Each chip is a different request, and the one that was made says nothing
    about the four that were not - a count taken from the page in hand would be
    the number of rows matching the *current* status, sitting under a chip
    promising another. Counting them all would be four more requests per render.
  */
  const chips: ReadonlyArray<ScopeChip<TaskScope>> = TASK_SCOPES.map((key) => ({
    key,
    label: t(`tasks.scope.tasks.${key}`),
  }));

  /*
    Above the state switch, so it is mounted whatever the read did. The chip is
    part of the request, which makes an empty or failed result something the
    chip itself may have caused - and a control that unmounts on the result it
    produced is one a manager cannot undo without editing the URL.
  */
  const scopeRow = (
    <div className={styles.scopeRow}>
      <ScopeChips
        chips={chips}
        selected={scope}
        /*
          The default chip comes back as `undefined`, which TanStack drops from
          the URL - so the tab's own default view carries no param.

          `Unassigned` also drops the selector, in this same navigation rather
          than in a second one. The pair is a contradiction the layout's
          `validateTaskFiltersSearch` resolves by moving the chip to
          `All Statuses` (see `resolveScope`), which is the right answer to a
          pasted URL and the wrong one to a click: someone pressing this chip is
          asking for the work nobody holds, so the selector is what they mean to
          let go of. Clearing it here is what makes the chip they pressed the one
          that lights up.
        */
        onSelect={(key) =>
          setSearch({
            scope: validateTaskScope(key),
            ...(key === TaskScope.UNASSIGNED ? { selector: undefined } : {}),
          })
        }
        ariaLabel={t("tasks.scope.ariaLabel")}
        overrideNote={
          state.kind === "loaded" && state.value.searchOverride
            ? t("tasks.search.override")
            : undefined
        }
      />
    </div>
  );

  switch (state.kind) {
    case "loading":
      return (
        <>
          {scopeRow}
          <div className={styles.loading} data-test-role="tasks-loading">
            <Loader type="page" />
          </div>
        </>
      );
    case "failed":
      return (
        <>
          {scopeRow}
          <div className={styles.error} data-test-role="tasks-failed">
            <AppError
              description={t("tasks.all.failed")}
              resolution={t("tasks.failureSupport")}
            />
          </div>
        </>
      );
    case "loaded":
      break;
  }

  const model = state.value;

  return (
    <>
      {scopeRow}

      {/*
        Said on every page, not only an empty one: a manager reading a short list
        cannot tell released-and-finished from never-released, and this listing
        can only answer the first.
      */}
      <p className={styles.deferredNote} data-test-role="tasks-released-note">
        {t("tasks.all.releasedOnly")}
      </p>

      {/*
        Below the chips and directly above the table, rather than inside the
        card's header beside the count: the two answer different questions - the
        count is how much matches, the pager is which of it is on screen - and
        the plans tab has no card to put one in. One position on both tabs is
        what makes walking pages the same gesture whichever is open.
      */}
      <div className={styles.pagerRow} data-test-role="pager-row">
        <ListPager
          pagination={model.pagination}
          isFetching={query.isFetching}
          // The cursor pair only; the filters and the chip are the layout's and
          // are left exactly as they stand.
          onChange={(next) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                cursor: next.cursor,
                dir: next.dir,
              }),
            })
          }
          t={t}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          {/* Named after the scope on show, not "All tasks" - a header that
              says "All tasks 16" above a filtered table is a claim about
              the warehouse, and a wrong one. The chip already says which
              scope; the header agreeing with it costs nothing. */}
          <h2 className={styles.cardHeading}>
            {t(
              `tasks.scope.tasks.${
                // A search has already widened the request past the selected
                // chip, so the chip is not what is on screen.
                model.searchOverride ? TaskScope.ALL : scope
              }`,
            )}
            {/* The warehouse's count, across every page - which is why it does
                not change when someone walks to page 2. Omitted rather than
                zeroed when the envelope does not report one. */}
            {model.totalCount === null ? (
              <></>
            ) : (
              <span className={styles.cardCount}>{model.totalCount}</span>
            )}
          </h2>
        </div>

        {model.rows.length > 0 ? (
          <div className={styles.tableScroll}>
            <TaskTable
              rows={model.rows}
              ariaLabel={t("tasks.all.ariaLabel")}
              showPlan={true}
              onSelect={drawer.select}
              t={t}
            />
          </div>
        ) : (
          <div
            className={`${styles.empty} ${styles.emptyFlush}`}
            data-test-role="tasks-empty"
          >
            {t("tasks.all.empty")}
          </div>
        )}
      </div>

      {drawer.selected ? (
        <TaskDetailDrawer
          orgId={orgId}
          planId={drawer.selected.identity.planId}
          taskId={drawer.selected.identity.taskId}
          task={drawer.selected.task}
          params={{ lang, org, location }}
          onClose={drawer.close}
          t={t}
        />
      ) : (
        <></>
      )}
    </>
  );
}
